import React, { useState } from "react";
import newId from "../../../newid";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import "@react-pdf-viewer/core/lib/styles/index.css";
import PropTypes from "prop-types";
import { withRouter } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { useArticleCategories, useCreateArticle } from "../../../hooks/useArticles";
import { toast, ToastContainer } from "react-toastify";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
} from "reactstrap";

const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024;

function createParagraph() {
  return {
    id: newId(),
    subtitle: "",
    content: "",
    paragraph_pic: null,
  };
}

function getImagePreview(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  return URL.createObjectURL(value);
}

function isImageTooLarge(file) {
  return Boolean(file && file.size > MAX_IMAGE_SIZE_BYTES);
}

const ArticlesAdd = (props) => {
  const history = useHistory();
  const { data: categoryList = [] } = useArticleCategories();
  const createArticleMutation = useCreateArticle();

  document.title = props.t("Ajout d'article | Le Conseiller du BTP");

  const [articleData, setArticleData] = useState({
    title: "",
    category: "",
    article_pic: null,
  });
  const [paragraphs, setParagraphs] = useState([createParagraph()]);
  const [errors, setErrors] = useState({});

  const notifyFileTooLarge = (label) => {
    toast.error(
      props.t(
        "{{label}} trop lourde: maximum 10 Mo. Veuillez choisir une image plus legere.",
        { label },
      ),
      { autoClose: 4000 },
    );
  };

  const updateParagraph = (id, field, value) => {
    setParagraphs((prev) =>
      prev.map((paragraph) =>
        paragraph.id === id ? { ...paragraph, [field]: value } : paragraph,
      ),
    );
  };

  const removeParagraph = (id) => {
    setParagraphs((prev) =>
      prev.length > 1 ? prev.filter((paragraph) => paragraph.id !== id) : prev,
    );
  };

  const validate = () => {
    const nextErrors = {};
    if (!articleData.title.trim()) {
      nextErrors.title = props.t("Le titre est requis");
    }
    if (!articleData.category) {
      nextErrors.category = props.t("La catégorie est requise");
    }
    if (isImageTooLarge(articleData.article_pic)) {
      nextErrors.article_pic = props.t(
        "L'image de couverture depasse la limite de 10 Mo.",
      );
    }

    const nonEmptyParagraphs = paragraphs.filter(
      (paragraph) =>
        paragraph.subtitle.trim() ||
        paragraph.content.trim() ||
        paragraph.paragraph_pic,
    );

    if (nonEmptyParagraphs.length === 0) {
      nextErrors.paragraphs = props.t(
        "Ajoutez au moins un paragraphe avec du contenu.",
      );
    } else {
      const invalidParagraph = nonEmptyParagraphs.find(
        (paragraph) => !paragraph.content.trim(),
      );
      if (invalidParagraph) {
        nextErrors.paragraphs = props.t(
          "Chaque paragraphe ajouté doit avoir un contenu.",
        );
      }

      const oversizedParagraphImage = nonEmptyParagraphs.find((paragraph) =>
        isImageTooLarge(paragraph.paragraph_pic),
      );
      if (oversizedParagraphImage) {
        nextErrors.paragraphs = props.t(
          "Une image de paragraphe depasse la limite de 10 Mo.",
        );
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const submitHandler = (event) => {
    event.preventDefault();
    if (!validate()) return;

    const payload = {
      title: articleData.title.trim(),
      category_id: Number(articleData.category),
      article_pic: articleData.article_pic || null,
      paragraphs: paragraphs
        .filter(
          (paragraph) =>
            paragraph.subtitle.trim() ||
            paragraph.content.trim() ||
            paragraph.paragraph_pic,
        )
        .map((paragraph) => ({
          subtitle: paragraph.subtitle.trim() || "",
          content: paragraph.content.trim(),
          paragraph_pic: paragraph.paragraph_pic || null,
        })),
    };

    createArticleMutation.mutate(payload, {
      onSuccess: () => {
        history.push("/articles");
      },
    });
  };

  return (
    <div className="page-content">
      <Container fluid>
        <ToastContainer closeButton={false} />
        <BreadCrumb
          title={props.t("Ajout d'article")}
          pageTitle={props.t("Articles")}
        />

        <Form encType="multipart/form-data" onSubmit={submitHandler}>
          <Row className="g-4">
            <Col xl={12}>
              <Alert color="info" className="mb-0">
                {props.t(
                  "Informations upload: formats image acceptes, taille maximale 10 Mo par image (couverture et paragraphes).",
                )}
              </Alert>
            </Col>

            <Col xl={12}>
              <Card>
                <CardBody>
                  <h5 className="mb-3">{props.t("Métadonnées de l'article")}</h5>
                  <Row className="g-3">
                    <Col xl={12}>
                      <Label className="form-label">
                        {props.t("Titre")} <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="text"
                        value={articleData.title}
                        onChange={(event) =>
                          setArticleData((prev) => ({
                            ...prev,
                            title: event.target.value,
                          }))
                        }
                        placeholder={props.t("Donnez un titre clair à l'article")}
                      />
                      {errors.title ? (
                        <small className="text-danger">{errors.title}</small>
                      ) : null}
                    </Col>
                    <Col xl={6}>
                      <Label className="form-label">
                        {props.t("Catégorie")} <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="select"
                        value={articleData.category}
                        onChange={(event) =>
                          setArticleData((prev) => ({
                            ...prev,
                            category: event.target.value,
                          }))
                        }
                      >
                        <option value="">
                          {props.t("Sélectionner une catégorie d'articles")}
                        </option>
                        {categoryList.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.designation}
                          </option>
                        ))}
                      </Input>
                      {errors.category ? (
                        <small className="text-danger">{errors.category}</small>
                      ) : null}
                    </Col>
                    <Col xl={6}>
                      <Label className="form-label">{props.t("Image de couverture")}</Label>
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(event) => {
                          const file = event.target.files?.[0] || null;
                          if (isImageTooLarge(file)) {
                            notifyFileTooLarge(props.t("Image de couverture"));
                            event.target.value = "";
                            return;
                          }
                          setArticleData((prev) => ({
                            ...prev,
                            article_pic: file,
                          }));
                        }}
                      />
                      {errors.article_pic ? (
                        <small className="text-danger d-block mt-1">
                          {errors.article_pic}
                        </small>
                      ) : null}
                      {articleData.article_pic ? (
                        <img
                          src={getImagePreview(articleData.article_pic)}
                          alt={props.t("Aperçu image de couverture")}
                          className="mt-2 rounded border"
                          style={{ maxWidth: "220px", maxHeight: "140px", objectFit: "cover" }}
                        />
                      ) : null}
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>

            <Col xl={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">{props.t("Contenu de l'article")}</h5>
                    <Button
                      type="button"
                      color="soft-success"
                      onClick={() => setParagraphs((prev) => [...prev, createParagraph()])}
                    >
                      <i className="ri-add-fill me-1 align-bottom"></i>
                      {props.t("Ajouter un paragraphe")}
                    </Button>
                  </div>

                  {errors.paragraphs ? (
                    <Alert color="danger" className="py-2">
                      {errors.paragraphs}
                    </Alert>
                  ) : null}

                  <div className="d-flex flex-column gap-3">
                    {paragraphs.map((paragraph, index) => (
                      <Card key={paragraph.id} className="border border-light shadow-none mb-0">
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0">
                              {props.t("Paragraphe")} {index + 1}
                            </h6>
                            <Button
                              type="button"
                              color="danger"
                              size="sm"
                              onClick={() => removeParagraph(paragraph.id)}
                              disabled={paragraphs.length === 1}
                            >
                              {props.t("Supprimer")}
                            </Button>
                          </div>
                          <Row className="g-3">
                            <Col xl={12}>
                              <Label className="form-label">{props.t("Sous-titre")}</Label>
                              <Input
                                type="text"
                                value={paragraph.subtitle}
                                onChange={(event) =>
                                  updateParagraph(
                                    paragraph.id,
                                    "subtitle",
                                    event.target.value,
                                  )
                                }
                                placeholder={props.t("Optionnel")}
                              />
                            </Col>
                            <Col xl={12}>
                              <Label className="form-label">
                                {props.t("Contenu")} <span className="text-danger">*</span>
                              </Label>
                              <Input
                                type="textarea"
                                rows={6}
                                value={paragraph.content}
                                onChange={(event) =>
                                  updateParagraph(
                                    paragraph.id,
                                    "content",
                                    event.target.value,
                                  )
                                }
                                placeholder={props.t("Rédigez le contenu du paragraphe")}
                              />
                            </Col>
                            <Col xl={12}>
                              <Label className="form-label">
                                {props.t("Image du paragraphe")}
                              </Label>
                              <Input
                                type="file"
                                accept="image/*"
                                onChange={(event) => {
                                  const file = event.target.files?.[0] || null;
                                  if (isImageTooLarge(file)) {
                                    notifyFileTooLarge(props.t("Image du paragraphe"));
                                    event.target.value = "";
                                    return;
                                  }
                                  updateParagraph(
                                    paragraph.id,
                                    "paragraph_pic",
                                    file,
                                  );
                                }}
                              />
                              {paragraph.paragraph_pic ? (
                                <img
                                  src={getImagePreview(paragraph.paragraph_pic)}
                                  alt={props.t("Aperçu image paragraphe")}
                                  className="mt-2 rounded border"
                                  style={{
                                    maxWidth: "220px",
                                    maxHeight: "140px",
                                    objectFit: "cover",
                                  }}
                                />
                              ) : null}
                            </Col>
                          </Row>
                        </CardBody>
                      </Card>
                    ))}
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col xl={12}>
              <div className="d-flex justify-content-end">
                <Button
                  type="submit"
                  color="warning"
                  disabled={createArticleMutation.isPending}
                >
                  {createArticleMutation.isPending
                    ? props.t("Soumission...")
                    : props.t("Soumettre l'article")}
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};

ArticlesAdd.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
};
export default withRouter(withTranslation()(ArticlesAdd));
